import { Icon } from '@mxenabled/mx-icons'
import { H3, P } from '@mxenabled/mxui'

import Button, { type ButtonProps } from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { useScreenSize } from '../hooks'

interface CurrencyDialogProps {
  closeAriaLabel: string
  content?: string
  inputLabel?: string
  inputValue?: string
  isOpen: boolean
  handleInputChange?: (value: string) => void
  handleModalClose: () => void
  handlePrimaryAction: () => void
  handleSecondaryAction?: () => void
  primaryColor?: ButtonProps['color']
  primaryText: string
  secondaryColor?: ButtonProps['color']
  secondaryText?: string
  title: string
}

// Created for V2 of BudgetDetailsDrawer.
// The previous design will be replaced once the new design is finalized.
// Also needs to be updated to use the mxui library
// with the updated Dialog components.

const CurrencyDialog = ({
  closeAriaLabel,
  content,
  inputLabel,
  inputValue,
  isOpen,
  handleInputChange,
  handleModalClose,
  handlePrimaryAction,
  handleSecondaryAction,
  primaryColor = 'primary',
  primaryText,
  secondaryColor = 'primary',
  secondaryText,
  title,
}: CurrencyDialogProps) => {
  const numericValue = parseFloat(inputValue ?? '0')
  const isValid = inputValue !== '' && inputValue !== '0' && !isNaN(numericValue)

  const { isSmallMobile } = useScreenSize()

  return (
    <Dialog
      aria-label={title}
      aria-labelledby="modal-title"
      maxWidth="xs"
      onClose={handleModalClose}
      open={isOpen}
      role="dialog"
      sx={{
        '& .MuiDialogContent-root': { p: 0 },
        ...(isSmallMobile && { '& .MuiDialog-paper': { minWidth: '100%' } }),
      }}
    >
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handlePrimaryAction()
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'space-between',
              pb: 12,
              pl: 24,
              position: 'relative',
              pr: 16,
              pt: 24,
            }}
          >
            <H3 id="modal-title">{title}</H3>
            <IconButton
              aria-label={closeAriaLabel}
              onClick={handleModalClose}
              sx={{ minHeight: 24, position: 'absolute', right: 8, top: 18 }}
            >
              <Icon name="close" sx={{ color: 'action.active' }} />
            </IconButton>
          </Stack>
          <Stack sx={{ gap: 24, px: 24 }}>
            <P variant="subtitle1">{content}</P>
            {inputLabel && (
              <TextField
                autoFocus={true}
                inputMode="decimal"
                label={inputLabel}
                onChange={({ target: { value } }) => {
                  if (!/^\d*\.?\d*$/.test(value)) return

                  handleInputChange?.(value)
                }}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                  htmlInput: {
                    'aria-label': inputLabel,
                    noValidate: true,
                    pattern: '[0-9]*\\.?[0-9]*',
                  },
                }}
                type="text"
                value={inputValue}
              />
            )}
          </Stack>
          <DialogActions
            sx={{
              justifyContent: 'flex-end',
              gap: 16,
              p: 24,
              '>:not(style)~:not(style)': { ml: 0 },
            }}
          >
            {secondaryText && (
              <Button
                aria-label={secondaryText}
                color={secondaryColor}
                onClick={handleSecondaryAction}
              >
                {secondaryText}
              </Button>
            )}
            <Button
              aria-label={primaryText}
              color={primaryColor}
              disabled={inputLabel ? !isValid : false}
              onClick={handlePrimaryAction}
              variant="contained"
            >
              {primaryText}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default CurrencyDialog
